import type { RecipientGroupType } from '@api';
import {
  ActionsCell,
  AddActionIcon,
  RecipientsSummary,
  TouringCard,
  TouringCardActions,
  TouringCardHeader,
  TouringCardSection,
  TouringCardTitle,
  TouringDataTable,
} from '@common';
import { useTranslation } from 'react-i18next';
import { GroupLabelCell } from './components';

type RecipientGroupsTablePropsType = {
  groups: RecipientGroupType[];
  canEdit: boolean;
  onAdd: () => void;
  onEdit: (group: RecipientGroupType) => void;
  onDelete: (groupId: string) => void;
};

export const RecipientGroupsTable: React.FC<RecipientGroupsTablePropsType> = ({
  groups,
  canEdit,
  onAdd,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation();

  return (
    <TouringCard data-testid="recipient-groups-container">
      <TouringCardHeader>
        <TouringCardTitle>
          {t('REPORTS.RECIPIENT_GROUPS.TITLE')}
        </TouringCardTitle>
        {canEdit && (
          <TouringCardActions>
            <AddActionIcon
              onClick={onAdd}
              ariaLabel={t('REPORTS.RECIPIENT_GROUPS.ADD')}
            />
          </TouringCardActions>
        )}
      </TouringCardHeader>
      <TouringCardSection>
        <TouringDataTable<RecipientGroupType>
          records={groups}
          noRecordsText={t('REPORTS.RECIPIENT_GROUPS.EMPTY')}
          withTableBorder={false}
          withColumnBorders={false}
          highlightOnHover={false}
          columns={[
            {
              accessor: 'label',
              title: t('REPORTS.RECIPIENT_GROUPS.TABLE_COLUMNS.GROUP'),
              render: ({ label, onlyCcRecipientSet }) => (
                <GroupLabelCell
                  label={label}
                  onlyCcRecipientSet={onlyCcRecipientSet}
                />
              ),
            },
            {
              accessor: 'recipients',
              title: t('REPORTS.RECIPIENT_GROUPS.TABLE_COLUMNS.RECIPIENTS'),
              render: ({ personRecipients, customRecipients }) => (
                <RecipientsSummary
                  recipients={[
                    ...personRecipients.map(({ id, emailField, person }) => ({
                      id,
                      emailField,
                      name: `${person.firstname} ${person.lastname}`,
                      email: person.email,
                    })),
                    ...customRecipients,
                  ]}
                />
              ),
            },
            {
              accessor: 'actions',
              title: t('REPORTS.RECIPIENT_GROUPS.TABLE_COLUMNS.ACTIONS'),
              titleStyle: { textAlign: 'center' },
              width: 100,
              hidden: !canEdit,
              render: (group) => (
                <ActionsCell.Root>
                  <ActionsCell.Edit onClick={() => onEdit(group)} />
                  <ActionsCell.Delete onClick={() => onDelete(group.id)} />
                </ActionsCell.Root>
              ),
            },
          ]}
        />
      </TouringCardSection>
    </TouringCard>
  );
};
